import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { AutoLocationField } from '../../../../../../../common/auto-location-field/AutoLocationField';

const TIMEZONE_OPTIONS = [
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Asia/Singapore',
  'UTC',
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toggleButton: {
      border: '1px solid',
      borderColor: '#dcdcdc',
      borderRadius: '4px',
      textTransform: 'none',
      padding: '7px 12px',
      fontWeight: 600,
      fontSize: '13px',
      color: '#666',
      backgroundColor: '#fff',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    toggleButtonSelected: {
      color: '#FCFCFC !important',
      backgroundColor: `${theme.palette.primary.main} !important`,
      borderColor: `${theme.palette.primary.main} !important`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.dark} !important`,
      },
    },
  })
);

interface SlotFormValues {
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  maxCapacity: number;
  locationType: 'local' | 'remote';
  remoteLocation: string;
  message: string;
  assignedManagerIds: string[];
}

const validationSchema = Yup.object().shape({
  date: Yup.string().required('Date is required'),
  startTime: Yup.string().required('Start time is required'),
  endTime: Yup.string().required('End time is required'),
  timezone: Yup.string().required('Timezone is required'),
  maxCapacity: Yup.number()
    .min(1, 'Must be at least 1')
    .required(),
});

type ManagerOption = {
  managerId: string;
  profile: { preferredName: string; lastName: string; avatarUrl: string | null };
};

interface AddBookableSlotDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<any> | void;
  taskId: string;
  availableManagers?: ManagerOption[];
  editSlot?: {
    bookableTimeSlotId: string;
    startDateTime: string;
    endDateTime: string | null;
    timezone: string;
    maxCapacity: number;
    remoteLocation: string | null;
    message: string | null;
    assignedManagerIds?: string[];
  } | null;
}

export function AddBookableSlotDialog({
  open,
  onClose,
  onSaved,
  taskId,
  availableManagers = [],
  editSlot,
}: AddBookableSlotDialogProps) {
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const saveFetch = useCampfireFetch({ defer: true });

  const getInitialValues = (): SlotFormValues => {
    if (editSlot) {
      const startDt = unpackToDateTime(editSlot.startDateTime);
      const endDt = editSlot.endDateTime ? unpackToDateTime(editSlot.endDateTime) : null;
      return {
        date: startDt.toFormat('yyyy-MM-dd'),
        startTime: startDt.toFormat('HH:mm'),
        endTime: endDt ? endDt.toFormat('HH:mm') : '',
        timezone: editSlot.timezone,
        maxCapacity: editSlot.maxCapacity,
        locationType: editSlot.remoteLocation ? 'remote' : 'local',
        remoteLocation: editSlot.remoteLocation ?? '',
        message: editSlot.message ?? '',
        assignedManagerIds: editSlot.assignedManagerIds ?? [],
      };
    }
    return {
      date: '',
      startTime: '09:00',
      endTime: '10:00',
      timezone: 'Australia/Brisbane',
      maxCapacity: 1,
      locationType: 'remote',
      remoteLocation: '',
      message: '',
      assignedManagerIds: [],
    };
  };

  const handleSubmit = async (values: SlotFormValues) => {
    const startDateTime = `${values.date}T${values.startTime}`;
    const endDateTime = values.endTime ? `${values.date}T${values.endTime}` : undefined;

    const payload: any = {
      taskId,
      startDateTime,
      endDateTime,
      timezone: values.timezone,
      maxCapacity: values.maxCapacity,
      assignedManagerIds: values.assignedManagerIds.length > 0 ? values.assignedManagerIds : undefined,
    };

    if (editSlot) {
      payload.bookableTimeSlotId = editSlot.bookableTimeSlotId;
    }
    if (values.locationType === 'remote' && values.remoteLocation) {
      payload.remoteLocation = values.remoteLocation;
    }
    if (values.message) {
      payload.message = values.message;
    }

    try {
      await saveFetch.run({
        url: `${apiUrl}/vm/task/event/bookable-slot/save`,
        method: 'post',
        data: payload,
      });
      setSnackbar({ open: true, message: editSlot ? 'Time slot updated' : 'Time slot added', variant: 'success' });
      await onSaved();
      onClose();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save time slot', variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' style={{ fontWeight: 600 }}>
            {editSlot ? 'Edit Time Slot' : 'Add Time Slot'}
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Formik initialValues={getInitialValues()} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Date'
                    type='date'
                    name='date'
                    variant='outlined'
                    size='small'
                    value={values.date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.date && Boolean(errors.date)}
                    helperText={touched.date && errors.date}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label='Start Time'
                    type='time'
                    name='startTime'
                    variant='outlined'
                    size='small'
                    value={values.startTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.startTime && Boolean(errors.startTime)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label='End Time'
                    type='time'
                    name='endTime'
                    variant='outlined'
                    size='small'
                    value={values.endTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.endTime && Boolean(errors.endTime)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label='Timezone'
                    name='timezone'
                    variant='outlined'
                    size='small'
                    value={values.timezone}
                    onChange={handleChange}
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label='Max Capacity'
                    type='number'
                    name='maxCapacity'
                    variant='outlined'
                    size='small'
                    value={values.maxCapacity}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                    error={touched.maxCapacity && Boolean(errors.maxCapacity)}
                  />
                </Grid>

                {/* Location Type Toggle */}
                <Grid item xs={12}>
                  <Typography variant='body2' style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                    Location Type
                  </Typography>
                  <Box display='flex' width='100%'>
                    <Button
                      fullWidth
                      className={`${classes.toggleButton} ${
                        values.locationType === 'local' ? classes.toggleButtonSelected : ''
                      }`}
                      onClick={() => setFieldValue('locationType', 'local')}
                      style={{ marginRight: '8px' }}
                    >
                      Specific Location
                    </Button>
                    <Button
                      fullWidth
                      className={`${classes.toggleButton} ${
                        values.locationType === 'remote' ? classes.toggleButtonSelected : ''
                      }`}
                      onClick={() => setFieldValue('locationType', 'remote')}
                    >
                      Remote / Virtual
                    </Button>
                  </Box>
                </Grid>

                {values.locationType === 'local' ? (
                  <Grid item xs={12}>
                    <AutoLocationField name='location' label='Location' fullWidth variant='outlined' helper />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Remote Location Details'
                      name='remoteLocation'
                      variant='outlined'
                      size='small'
                      multiline
                      rows={2}
                      value={values.remoteLocation}
                      onChange={handleChange}
                      placeholder='Meeting link, phone number, or instructions'
                    />
                  </Grid>
                )}

                {/* Manager Selection */}
                {availableManagers.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant='body2' style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                      Assigned Managers
                    </Typography>
                    <Box display='flex' flexWrap='wrap' style={{ gap: '6px', marginBottom: '8px' }}>
                      {values.assignedManagerIds.map((mId) => {
                        const mgr = availableManagers.find((m) => m.managerId === mId);
                        if (!mgr) return null;
                        return (
                          <Chip
                            key={mId}
                            label={`${mgr.profile.preferredName} ${mgr.profile.lastName}`}
                            size='small'
                            onDelete={() =>
                              setFieldValue(
                                'assignedManagerIds',
                                values.assignedManagerIds.filter((id) => id !== mId)
                              )
                            }
                          />
                        );
                      })}
                    </Box>
                    <TextField
                      select
                      fullWidth
                      variant='outlined'
                      size='small'
                      value=''
                      label='Add a manager'
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id && !values.assignedManagerIds.includes(id)) {
                          setFieldValue('assignedManagerIds', [...values.assignedManagerIds, id]);
                        }
                      }}
                    >
                      {availableManagers
                        .filter((m) => !values.assignedManagerIds.includes(m.managerId))
                        .map((m) => (
                          <MenuItem key={m.managerId} value={m.managerId}>
                            {m.profile.preferredName} {m.profile.lastName}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Message'
                    name='message'
                    variant='outlined'
                    size='small'
                    multiline
                    rows={2}
                    value={values.message}
                    onChange={handleChange}
                    placeholder='Instructions shown to volunteers'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px' }}>
              <TabletButton variant='outlined' onClick={onClose} disabled={isSubmitting}>
                Cancel
              </TabletButton>
              <TabletButton variant='contained' color='primary' type='submit' disabled={isSubmitting}>
                {editSlot ? 'Update' : 'Add Slot'}
              </TabletButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
